/**
 * Phase 3.27 — Marketing-Sektion.
 * Aggregiert Transaktions-Code + Stats-KPIs + Attribution-Tracking + Sub-Akkordeons
 * (Börsenexporte, Sozialmedia, Zielgruppen, Werbemittel, Homepage-Embed).
 */
import { useEffect, useState } from "react";
import { Sparkles, Copy, Download, QrCode, ShoppingBag, Megaphone, Repeat, Share2, Globe, Link as LinkIcon } from "lucide-react";
import { QRCodeSVG } from "qrcode.react";
import { Button } from "../ui/button";
import { toast } from "sonner";
import api from "../../lib/api";
import {
    ExchangeExportSection,
    SocialMediaExportSection,
    SalePreferencesSection,
    WerbemittelSection,
    WhitelabelEmbedSection,
} from "../DealerSettings";
import { BranchSubToggle } from "./BranchOverrideToggle";
import { OrgLockedSubgroup } from "./OrgLockedSection";

function AttrCard({ label, total, testId }) {
    const sum = Object.values(total || {}).reduce((a, b) => a + (b || 0), 0);
    return (
        <div className="bg-white/5 border border-white/10 rounded-sm p-3">
            <div className="text-[9px] uppercase tracking-wider font-bold text-gray-300">{label}</div>
            <div className="font-mono-num text-lg font-black text-[#FFCC00]" data-testid={testId}>
                {sum}
            </div>
        </div>
    );
}

export function MarketingSection({ currentUser, excludeSections, transactionCode, bannerTitle, bannerSubtitle, hideQrSmiley, hideBanner }) {
    const HIDDEN = new Set(excludeSections || []);
    const [open, setOpen] = useState({ borsen: false, social: false, audience: false, homepage: false, werbemittel: false, leadgen: false, preistalk: false });
    const [stats, setStats] = useState(null);
    const [showQr, setShowQr] = useState(false);
    const [leadGenEnabled, setLeadGenEnabled] = useState(!!currentUser?.marketing_lead_generator);
    const [preistalkEnabled, setPreistalkEnabled] = useState(!!currentUser?.marketing_preistalk);
    // Phase 4.1 — Vermittler dürfen ihren `referral_short_code` als Transaktions-Code
    // mitgeben, da sie keine customer_number haben. Default: H_… Format für Händler.
    const code = transactionCode
        || (currentUser?.customer_number ? `H_${currentUser.customer_number}` : null);

    const toggleField = async (field, value, setter) => {
        setter(value);
        try {
            await api.patch("/users/me/preferences", { [field]: value });
            toast.success(value ? "Aktiviert" : "Deaktiviert");
        } catch (e) {
            setter(!value);
            toast.error(e.response?.data?.detail || "Speichern fehlgeschlagen");
        }
    };

    useEffect(() => {
        let alive = true;
        api.get("/users/me/marketing-stats")
            .then((r) => { if (alive) setStats(r.data); })
            .catch(() => { if (alive) setStats(null); });
        return () => { alive = false; };
    }, []);

    const copyCode = async () => {
        if (!code) return;
        try {
            await navigator.clipboard.writeText(code);
            toast.success("Transaktions-Code kopiert");
        } catch {
            toast.error("Bitte Code manuell markieren");
        }
    };

    const downloadQr = () => {
        const svg = document.querySelector('[data-testid="marketing-qr-svg"]');
        if (!svg || !code) { toast.error("QR-Code nicht gefunden"); return; }
        const blob = new Blob([new XMLSerializer().serializeToString(svg)], { type: "image/svg+xml" });
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `transaktionscode-${code}.svg`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
        toast.success("QR-Code heruntergeladen");
    };

    const Header = ({ id, icon: Icon, title, hint, hidden, right }) => {
        if (hidden) return null;
        return (
            <div className="flex items-center bg-gray-50 hover:bg-gray-100 border-b border-gray-200">
                <button
                    type="button"
                    onClick={() => setOpen((p) => ({ ...p, [id]: !p[id] }))}
                    className="flex-1 flex items-center gap-3 px-5 py-3 text-left min-w-0"
                    data-testid={`marketing-toggle-${id}`}
                    aria-expanded={open[id]}
                >
                    <Icon className="w-4 h-4 text-[#FFCC00] shrink-0" />
                    <div className="flex-1 min-w-0">
                        <div className="font-bold text-sm text-[#111827]">{title}</div>
                        <div className="text-[11px] text-gray-500">{hint}</div>
                    </div>
                    <span className={`text-gray-400 text-lg transition-transform ${open[id] ? "rotate-180" : ""}`} aria-hidden="true">⌄</span>
                </button>
                {right && <div className="pr-4 pl-2 shrink-0">{right}</div>}
            </div>
        );
    };

    return (
        <div className="bg-white border border-gray-200 rounded-sm overflow-hidden" data-testid="marketing-section">
            {!hideBanner && (
            <div className="px-6 py-5 bg-gradient-to-r from-[#111827] to-[#1F2937] text-white" data-testid="marketing-banner">
                <div className="flex items-center justify-between gap-4 flex-wrap">
                    <div>
                        <div className="flex items-center gap-2">
                            <Sparkles className="w-5 h-5 text-[#FFCC00]" />
                            <h2 className="font-display text-xl font-bold">{bannerTitle || "Marketing Die Zweiradhändler"}</h2>
                        </div>
                        <p className="text-xs text-gray-300 mt-1">
                            {bannerSubtitle || "Ihr persönlicher Transaktions-Code — verfolgt sämtliche Umsätze und sammelt automatisch Punkte aus unserem Tarif-System."}
                        </p>
                    </div>
                    {code && (
                        <div className="flex items-center gap-2">
                            <button
                                onClick={copyCode}
                                className="group flex items-center gap-2 px-4 py-3 bg-white/5 hover:bg-white/10 border-2 border-[#FFCC00] rounded-sm transition-colors"
                                data-testid="marketing-code-button"
                                title="Klick zum Kopieren"
                            >
                                <div>
                                    <div className="text-[10px] uppercase tracking-widest font-bold text-[#FFCC00] text-left">Transaktions-Code</div>
                                    <div className="font-mono-num text-2xl font-black tracking-wider" data-testid="marketing-code">
                                        {code}
                                    </div>
                                </div>
                                <Copy className="w-4 h-4 text-[#FFCC00] opacity-50 group-hover:opacity-100" />
                            </button>
                            <button
                                onClick={() => setShowQr((v) => !v)}
                                className="flex flex-col items-center justify-center px-3 py-3 bg-white/5 hover:bg-white/10 border-2 border-[#FFCC00] rounded-sm transition-colors text-[#FFCC00]"
                                data-testid="marketing-qr-toggle"
                                title="QR-Code anzeigen"
                                aria-pressed={showQr}
                            >
                                <QrCode className="w-5 h-5" />
                                <span className="text-[9px] uppercase tracking-wider font-bold mt-0.5">QR</span>
                            </button>
                            <a
                                href="https://twowheels.info/werbemittel"
                                target="_blank"
                                rel="noopener noreferrer"
                                className="flex flex-col items-center justify-center px-3 py-3 bg-[#FFCC00] hover:bg-[#E6B800] border-2 border-[#FFCC00] rounded-sm transition-colors text-[#111827]"
                                data-testid="marketing-werbemittel-link"
                                title="Werbemittel bestellen"
                            >
                                <ShoppingBag className="w-5 h-5" />
                                <span className="text-[9px] uppercase tracking-wider font-bold mt-0.5">Werbemittel</span>
                            </a>
                        </div>
                    )}
                </div>

                <div className="grid grid-cols-3 gap-3 mt-4" data-testid="marketing-kpis">
                    <div className="bg-white/5 border border-white/10 rounded-sm p-3">
                        <div className="text-[10px] uppercase tracking-wider font-bold text-gray-300">Erfasster Umsatz</div>
                        <div className="font-mono-num text-xl font-black text-[#FFCC00]" data-testid="marketing-revenue">
                            {stats == null
                                ? "—"
                                : (() => {
                                    const v = (stats.total_revenue_cents || 0) / 100;
                                    try {
                                        return new Intl.NumberFormat("de-DE", { style: "currency", currency: stats.currency || "EUR", minimumFractionDigits: v % 1 === 0 ? 0 : 2 }).format(v);
                                    } catch { return `${v.toFixed(2)} €`; }
                                })()
                            }
                        </div>
                    </div>
                    <div className="bg-white/5 border border-white/10 rounded-sm p-3">
                        <div className="text-[10px] uppercase tracking-wider font-bold text-gray-300">Erhaltene Punkte</div>
                        <div className="font-mono-num text-xl font-black text-[#FFCC00]" data-testid="marketing-points">
                            {stats == null ? "—" : (stats.points_earned ?? 0)}
                        </div>
                    </div>
                    <div className="bg-white/5 border border-white/10 rounded-sm p-3">
                        <div className="text-[10px] uppercase tracking-wider font-bold text-gray-300">Transaktionen</div>
                        <div className="font-mono-num text-xl font-black text-[#FFCC00]" data-testid="marketing-transactions">
                            {stats == null ? "—" : (stats.transaction_count ?? 0)}
                        </div>
                    </div>
                </div>
                <div className="mt-3 text-[10px] text-gray-400">
                    Punkte-Regel: 1 Punkt pro 1 € Umsatz. Punkte fließen in das Tarif-/Vermittler-Punktesystem ein.
                </div>

                {stats && stats.attribution && (
                    <div className="mt-4 pt-4 border-t border-white/10" data-testid="marketing-attribution-section">
                        <div className="text-[10px] uppercase tracking-wider font-bold text-gray-300 mb-2 flex items-center gap-2">
                            <Megaphone className="w-3 h-3" /> Werbemittel-Tracking
                        </div>
                        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                            <AttrCard label="Heute" testId="attr-today" total={stats.attribution.today} />
                            <AttrCard label="7 Tage" testId="attr-7d" total={stats.attribution.last_7d} />
                            <AttrCard label="30 Tage" testId="attr-30d" total={stats.attribution.last_30d} />
                            <div className="bg-white/5 border border-white/10 rounded-sm p-3">
                                <div className="text-[9px] uppercase tracking-wider font-bold text-gray-300">Unique Besucher 30d</div>
                                <div className="font-mono-num text-lg font-black text-[#FFCC00]" data-testid="attr-unique-visitors-30d">
                                    {stats.attribution.unique_visitors_30d ?? 0}
                                </div>
                            </div>
                        </div>
                        <div className="mt-3 grid grid-cols-2 sm:grid-cols-4 gap-2 text-[10px]" data-testid="attr-30d-breakdown">
                            {["banner", "landing", "qr", "pageview"].map((k) => (
                                <div key={k} className="flex items-center justify-between bg-white/5 border border-white/10 rounded-sm px-2 py-1.5" data-testid={`attr-30d-kind-${k}`}>
                                    <span className="uppercase tracking-wider text-gray-300">{k}</span>
                                    <span className="font-mono-num font-bold text-[#FFCC00]">{(stats.attribution.last_30d || {})[k] ?? 0}</span>
                                </div>
                            ))}
                        </div>
                        <div className="mt-3 flex items-center justify-between text-[10px] text-gray-400">
                            <span>Conversion-Rate 30d (Klicks → bezahlte Transaktionen):</span>
                            <span className="font-mono-num font-bold text-[#FFCC00]" data-testid="attr-conversion-rate-30d">
                                {((stats.conversion_rate_30d ?? 0) * 100).toFixed(2)}%
                            </span>
                        </div>
                    </div>
                )}

                {showQr && code && (
                    <div className="mt-4 p-4 bg-white text-[#111827] rounded-sm flex flex-col sm:flex-row items-center gap-4" data-testid="marketing-qr-panel">
                        <div className="bg-white p-3 rounded-sm border border-gray-200 shrink-0">
                            <QRCodeSVG
                                value={code}
                                size={160}
                                level="H"
                                bgColor="#FFFFFF"
                                fgColor="#111827"
                                data-testid="marketing-qr-svg"
                            />
                        </div>
                        <div className="flex-1 text-center sm:text-left">
                            <div className="font-bold text-sm mb-1">QR-Code Ihres Transaktions-Codes</div>
                            <p className="text-xs text-gray-600 mb-3">
                                Drucken Sie diesen Code auf Visitenkarten, Werkstatt-Aufsteller oder Belege —
                                Kunden scannen ihn, der Umsatz wird automatisch <span className="font-mono-num">{code}</span> zugeordnet
                                und Sie erhalten dafür Punkte.
                            </p>
                            <Button onClick={downloadQr} size="sm" className="bg-[#111827] hover:bg-black text-white rounded-sm font-bold uppercase tracking-wider text-xs" data-testid="marketing-qr-download">
                                <Download className="w-3.5 h-3.5 mr-1.5" /> SVG herunterladen
                            </Button>
                        </div>
                    </div>
                )}
            </div>
            )}

            <div className="p-1"></div>

            <Header id="borsen" icon={Repeat} title="Börsenexporte" hint="Veröffentlichung auf mobile.de, 1000PS, BikeBörse u. a." hidden={HIDDEN.has("borsen")} right={<BranchSubToggle area="marketing" subKey="borsen" currentUser={currentUser} fixedOff title="Börsenexporte" />} />
            {!HIDDEN.has("borsen") && open.borsen && (
                <div className="p-5 border-b border-gray-200" data-testid="marketing-borsen-body">
                    <ExchangeExportSection currentUser={currentUser} />
                </div>
            )}
            <Header id="social" icon={Share2} title="Sozialmedia" hint="WhatsApp · Instagram · TikTok · Facebook · YouTube · X" hidden={HIDDEN.has("social")} right={<BranchSubToggle area="marketing" subKey="social" currentUser={currentUser} />} />
            {!HIDDEN.has("social") && open.social && (
                <div className="p-5 border-b border-gray-200" data-testid="marketing-social-body">
                    <OrgLockedSubgroup area="marketing" subKey="social" label="Sozialmedia"><SocialMediaExportSection currentUser={currentUser} /></OrgLockedSubgroup>
                </div>
            )}
            <Header id="audience" icon={Globe} title="Zielgruppen" hint="B2B / B2C · Regional · Europaweit · International" hidden={HIDDEN.has("audience")} right={<BranchSubToggle area="marketing" subKey="audience" currentUser={currentUser} />} />
            {!HIDDEN.has("audience") && open.audience && (
                <div className="p-5 border-b border-gray-200" data-testid="marketing-audience-body">
                    <OrgLockedSubgroup area="marketing" subKey="audience" label="Zielgruppen"><SalePreferencesSection currentUser={currentUser} /></OrgLockedSubgroup>
                </div>
            )}
            <Header id="werbemittel" icon={Megaphone} title="Werbemittel & Tracking" hint="Banner, Landingpage, QR-Smiley und Werbung auf twowheels.info" hidden={HIDDEN.has("werbemittel")} right={<BranchSubToggle area="marketing" subKey="werbemittel" currentUser={currentUser} />} />
            {!HIDDEN.has("werbemittel") && open.werbemittel && (
                <div className="p-5 border-b border-gray-200" data-testid="marketing-werbemittel-body">
                    <div className="mb-3 flex flex-wrap items-center justify-between gap-2 p-3 bg-[#FFFBEB] border border-[#FFCC00] rounded-sm">
                        <span className="text-xs text-gray-700">
                            Tipp: Vollbild-Ansicht aller Werbemittel auf einer dedizierten Unterseite öffnen.
                        </span>
                        <a
                            href="/dealer/werbemittel"
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-[#111827] text-white text-xs font-bold uppercase tracking-wider rounded-sm hover:bg-black"
                            data-testid="marketing-werbemittel-fullpage-link"
                        >
                            Vollbild öffnen ↗
                        </a>
                    </div>
                    <OrgLockedSubgroup area="marketing" subKey="werbemittel" label="Werbemittel & Tracking"><WerbemittelSection currentUser={currentUser} hideQrSmiley={hideQrSmiley} /></OrgLockedSubgroup>
                </div>
            )}
            <Header id="homepage" icon={LinkIcon} title="Homepage" hint="Inserate per iFrame in Ihre eigene Webseite einbetten." hidden={HIDDEN.has("homepage")} right={<BranchSubToggle area="marketing" subKey="homepage" currentUser={currentUser} />} />
            {!HIDDEN.has("homepage") && open.homepage && (
                <div className="p-5" data-testid="marketing-homepage-body">
                    <OrgLockedSubgroup area="marketing" subKey="homepage" label="Homepage-Embed"><WhitelabelEmbedSection currentUser={currentUser} /></OrgLockedSubgroup>
                </div>
            )}

            <Header id="leadgen" icon={Sparkles} title="Lead Generator nutzen" hint="KI überwacht Chats, Foren & Kanäle und liefert Kaufabsicht-Signale" hidden={HIDDEN.has("leadgen")} right={<BranchSubToggle area="marketing" subKey="leadgen" currentUser={currentUser} />} />
            {!HIDDEN.has("leadgen") && open.leadgen && (
                <div className="p-5 border-b border-gray-200" data-testid="marketing-leadgen-body">
                    <OrgLockedSubgroup area="marketing" subKey="leadgen" label="Lead Generator">
                    <label className="flex items-start gap-3 cursor-pointer">
                        <input
                            type="checkbox"
                            checked={leadGenEnabled}
                            onChange={(e) => toggleField("marketing_lead_generator", e.target.checked, setLeadGenEnabled)}
                            className="mt-1 w-5 h-5 accent-[#FFCC00]"
                            data-testid="marketing-leadgen-checkbox"
                        />
                        <div className="flex-1">
                            <div className="font-bold text-[#111827]">Lead Generator aktivieren</div>
                            <p className="text-xs text-gray-600 mt-1">
                                Eine KI überwacht öffentlich zugängliche Chats, Foren und andere Kanäle nach
                                Kunden, die einen <strong>Kauf oder Verkauf planen</strong>. Die erkannten Signale
                                werden Ihnen als Leads in der Marktplatzeinstellung übermittelt — passend zu Ihren
                                gewählten Marken und Ihrer Region. DSGVO-konform: Es werden nur öffentliche Daten
                                ausgewertet, keine personenbezogenen Identifikatoren gespeichert.
                            </p>
                            {leadGenEnabled && (
                                <div className="mt-3 inline-flex items-center gap-2 px-3 py-1.5 bg-[#FFCC00]/15 border border-[#FFCC00]/40 rounded-sm text-[11px] font-bold text-[#111827]">
                                    <Sparkles className="w-3 h-3" /> Aktiv — eingehende Leads erscheinen in Ihrem Dashboard
                                </div>
                            )}
                        </div>
                    </label>
                    </OrgLockedSubgroup>
                </div>
            )}

            <Header id="preistalk" icon={Megaphone} title="PreisTalk" hint="Käufer-Preisverhandlung über eigene Landingpage & Forum" hidden={HIDDEN.has("preistalk")} right={<BranchSubToggle area="marketing" subKey="preistalk" currentUser={currentUser} />} />
            {!HIDDEN.has("preistalk") && open.preistalk && (
                <div className="p-5" data-testid="marketing-preistalk-body">
                    <OrgLockedSubgroup area="marketing" subKey="preistalk" label="PreisTalk">
                    <label className="flex items-start gap-3 cursor-pointer">
                        <input
                            type="checkbox"
                            checked={preistalkEnabled}
                            onChange={(e) => toggleField("marketing_preistalk", e.target.checked, setPreistalkEnabled)}
                            className="mt-1 w-5 h-5 accent-[#FFCC00]"
                            data-testid="marketing-preistalk-checkbox"
                        />
                        <div className="flex-1">
                            <div className="font-bold text-[#111827]">PreisTalk-Plattform aktivieren</div>
                            <p className="text-xs text-gray-600 mt-1">
                                B2C-Käufer können auf einer eigenen Landingpage + Forum den Preis für ein
                                <strong> vorkonfiguriertes Fahrzeug</strong> verhandeln. Sie als Händler legen
                                Modell, Ausstattung und Preis-Untergrenze fest — die Community handelt den finalen
                                Verkaufspreis aus. Erhöht die Sichtbarkeit Ihres Bestands und schafft Engagement
                                rund um Ihre Marken.
                            </p>
                            {preistalkEnabled && (
                                <div className="mt-3 inline-flex items-center gap-2 px-3 py-1.5 bg-[#FFCC00]/15 border border-[#FFCC00]/40 rounded-sm text-[11px] font-bold text-[#111827]">
                                    <Megaphone className="w-3 h-3" /> Aktiv — Ihre PreisTalk-Landingpage wird in Kürze freigeschaltet
                                </div>
                            )}
                        </div>
                    </label>
                    </OrgLockedSubgroup>
                </div>
            )}
        </div>
    );
}
